import * as React from 'react';
import { useTranslate } from 'react-admin';
import {
    Card,
    CardHeader,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Typography,
    Stack,
} from '@mui/material';

const clusterStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'available':
            return 'success';
        case 'creating':
        case 'modifying':
        case 'upgrading':
            return 'warning';
        case 'stopped':
        case 'stopping':
        case 'deleting':
            return 'error';
        default:
            return 'default';
    }
};

interface RdsClusterPanelProps {
    clusters: any[];
}

const RdsClusterPanel = ({ clusters }: RdsClusterPanelProps) => {
    const translate = useTranslate();

    return (
        <Card>
            <CardHeader title={translate('hallinta.rds.title')} />
            <CardContent>
                {clusters.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        {translate('hallinta.rds.no_clusters')}
                    </Typography>
                )}
                {clusters.length > 0 && (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {translate(
                                            'hallinta.rds.fields.cluster'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.rds.fields.status'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.rds.fields.engine'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.rds.fields.endpoint'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.rds.fields.readerEndpoint'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.rds.fields.instances'
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clusters.map((cluster: any) => (
                                    <TableRow key={cluster.id}>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                            >
                                                {cluster.clusterIdentifier}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={cluster.status}
                                                color={
                                                    clusterStatusColor(
                                                        cluster.status
                                                    ) as any
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {cluster.engine}{' '}
                                                {cluster.engineVersion}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {cluster.endpoint}:
                                                {cluster.port}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {cluster.readerEndpoint}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                            >
                                                {cluster.instances?.map(
                                                    (inst: any) => (
                                                        <Chip
                                                            key={inst.id}
                                                            label={`${inst.id}${inst.isWriter ? ' (W)' : ' (R)'}`}
                                                            size="small"
                                                            color={
                                                                inst.isWriter
                                                                    ? 'primary'
                                                                    : 'default'
                                                            }
                                                            variant={
                                                                inst.isWriter
                                                                    ? 'filled'
                                                                    : 'outlined'
                                                            }
                                                        />
                                                    )
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default RdsClusterPanel;
